import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.data?.detail === 'Email already subscribed') {
        toast.info('You are already subscribed to our newsletter');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-black border-t border-white/5">
      {/* Newsletter Section */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="font-heading text-3xl sm:text-4xl text-white mb-4">
                Stay <span className="text-gold-500">Informed</span>
              </h3>
              <p className="font-body text-white/60 text-base">
                Subscribe to our newsletter for wealth-building insights, event updates, and exclusive content.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                data-testid="newsletter-email-input"
                className="flex-1 bg-white/5 border border-white/10 px-6 py-4 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors duration-300"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="newsletter-submit-btn"
                className="bg-gold-500 text-black px-8 py-4 font-body font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_wealth-builders-6/artifacts/v43z5dyx_ChatGPT%20Image%20Mar%201%2C%202026%2C%2006_17_47%20PM.png"
                alt="Empower Wealth Builders"
                className="h-24 w-auto object-contain"
              />
            </Link>
            <p className="font-body text-white/50 text-sm leading-relaxed mb-6">
              Empowering individuals and families to build lasting wealth through education, mentorship, and proven strategies.
            </p>
            <div className="flex gap-4">
              <a href="#" data-testid="social-linkedin" className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/50 hover:text-gold-500 hover:border-gold-500/50 transition-colors duration-300">
                <Linkedin size={18} />
              </a>
              <a href="#" data-testid="social-twitter" className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/50 hover:text-gold-500 hover:border-gold-500/50 transition-colors duration-300">
                <Twitter size={18} />
              </a>
              <a href="#" data-testid="social-instagram" className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/50 hover:text-gold-500 hover:border-gold-500/50 transition-colors duration-300">
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg text-white mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {['Home', 'About', 'Services', 'Join Our Team'].map((link) => (
                <li key={link}>
                  <Link
                    to={link === 'Home' ? '/' : link === 'Join Our Team' ? '/careers' : `/${link.toLowerCase()}`}
                    className="font-body text-sm text-white/50 hover:text-gold-500 transition-colors duration-300"
                  >
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg text-white mb-6">Our Services</h4>
            <ul className="space-y-4">
              {['Financial Planning', 'Wealth Building', 'Leadership Development', 'Mentorship Programs'].map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="font-body text-sm text-white/50 hover:text-gold-500 transition-colors duration-300"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                <span className="font-body text-sm text-white/50">
                  100 Milverton Drive, Mississauga, L5R 4H1, Ontario
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-gold-500 flex-shrink-0" />
                <a href="tel:+14379957262" className="font-body text-sm text-white/50 hover:text-gold-500 transition-colors duration-300">
                  437-995-7262
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-gold-500 flex-shrink-0" />
                <a href="mailto:Info@empowerwealthbuilder.com" className="font-body text-sm text-white/50 hover:text-gold-500 transition-colors duration-300">
                  Info@empowerwealthbuilder.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-sm text-white/40">
              © {new Date().getFullYear()} Empower Wealth Builders. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="font-body text-sm text-white/40 hover:text-gold-500 transition-colors duration-300">
                Privacy Policy
              </a>
              <a href="#" className="font-body text-sm text-white/40 hover:text-gold-500 transition-colors duration-300">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
